<template lang="pug">
#waiter-family-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 家族メールユーザー情報登録

    .box-body.register-body
      .register-row.caution-message
        .register-group.font-strong-color ご家族用利用規約をご家族にお渡しいただき、ご家族に承諾いただいて登録してください。（ご家族用利用規約はマニュアルにあります。ご家族用メール配信申込書サンプルをご使用いただけます。）
      FormInput(
        key-name="家族氏名"
        required
        name="family-name"
        v-model="name"
        type="text"
        placeholder="家族氏名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")
      FormInputGender(key-name="性別" v-model="gender")
      FormInputSplitDate(
        key-name="生年月日"
        :year.sync="year"
        :month.sync="month"
        :day.sync="day"
        validationDateKeyName="生年月日")
      FormInput(
        key-name="電話番号"
        required
        name="tel"
        v-model="tel"
        type="text"
        placeholder="電話番号"
        option-text="-（ハイフン）なしで入力してください。"
        :maxlength="11"
        :validation-rules="{ required: true, numeric: true, max: 13, min: 5 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="続柄"
        required
        name="relation"
        v-model="relation"
        type="text" placeholder="続柄"
        :maxlength="10"
        :validation-rules="{ required: true, max: 10 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="役割"
        name="position"
        v-model="position"
        type="text" placeholder="役割"
        option-text="設定内容はご家族には公開されません。"
        :maxlength="32"
        :validation-rules="{ max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="メールアドレス"
        required
        name="email"
        v-model="email"
        type="text"
        placeholder="メールアドレス"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true, equal_value: confirmEmail }"
        :styles="{ width: '540px' }")
      FormInput(
        key-name="メールアドレス（確認）"
        required
        name="confirm-email"
        v-model="confirmEmail"
        type="text"
        placeholder="メールアドレス（確認）"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true }"
        :styles="{ width: '540px' }")
      .register-row.caution-message
        .register-group.font-strong-color メールアドレスが間違っていないか、登録後に登録確認メッセージを送信し、ご家族が受信できているかをお電話等でご確認ください。

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoWaiterDetail" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="家族ユーザー情報を登録しますか？" @ok="registerFamily")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoWaiterDetail")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, needRefreshesStore } from "@/store/store";
import { WaiterListState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentsService from "@/commons/services/Residents";
import MailFamiliesService, { MailFamiliesPostModel } from "@/commons/services/MailFamilies";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormInputGender from "@/pc/components/forms/FormInputGender.vue";
import FormInputSplitDate from "@/pc/components/forms/FormInputSplitDate.vue";

/**
 * 家族メールユーザー登録画面コンポーネント
 */
@Component({
  components: {
    FormInputSelect,
    FormInput,
    FormInputGender,
    FormInputSplitDate,
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterFamilyRegister extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 家族メールユーザーサービス */
  @Inject("MailFamiliesService") mailFamiliesService!: MailFamiliesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 家族名 */
  name: string = "";
  /** 生年月日・年 */
  year: string = "";
  /** 生年月日・月 */
  month: string = "";
  /** 生年月日・日 */
  day: string = "";
  /** 性別 */
  gender: string = "無回答";
  /** 電話番号 */
  tel: string = "";
  /** 続柄 */
  relation: string = "";
  /** 役割 */
  position: string = "";
  /** メールアドレス */
  email: string = "";
  /** メールアドレス(確認) */
  confirmEmail: string = "";

  /** APIから返却された場合に表示するエラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### 確認モーダルを表示する
   *
   * - バリデーターを使用してバリデーションする
   * - 生年月日のバリデーションは自前で行う
   * - バリデーションでエラーが無ければ確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const birthdayValidationResult: boolean = await this.validateBirthday();
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult && birthdayValidationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 生年月日の存在するかしないかのバリデーション
   *
   * - 生年月日が1つでも入力されていればバリデーション対象として存在確認する
   * - 生年月日が1つも入力されていない場合はバリデーションせず、OKとする
   */
  public async validateBirthday(): Promise<boolean> {
    this.$validator.errors.remove("date");

    if (this.year || this.month || this.day) {
      const birthday: number = this.joinBirthday();
      const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "生年月日", bails: false });
      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "date", msg: error });
        }
      }
      return valid;
    } else {
      return true;
    }
  }

  /**
   * ### 分割している生年月日を結合する
   *
   * - DBに登録する場合はnumber型で登録する設計になっている
   * - そのため月と日は1桁の場合は0埋めする
   */
  public joinBirthday(): number {
    let month: string = String(this.month);
    if (month.length == 1) {
      month = `0${month}`;
    }

    let day: string = String(this.day);
    if (day.length == 1) {
      day = `0${day}`;
    }

    const birthday: number = Number(`${this.year}${month}${day}000000000`);

    return birthday;
  }

  /**
   * ### 待機者詳細画面へ遷移する
   */
  public gotoWaiterDetail(): void {
    this.$router.push({
      name: PcUrl.WaiterDetail,
      params: { facilityid: this.$route.params.facilityid, idinsystem: this.$route.params.idinsystem, residentid: this.$route.params.residentid }
    });
    return;
  }

  /**
   * ### 家族メールユーザーを登録する
   */
  public async registerFamily(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    try {
      const residentId: number = Number(this.$route.params.residentid);
      const requestModel: MailFamiliesPostModel = this.createRequestModel();
      await this.mailFamiliesService.post(userStore, residentId, requestModel);

      // 待機者一覧を再読み込みさせるようstoreを更新
      let waiterList: WaiterListState = needRefreshesStore.waiterList;
      waiterList.waiterList = true;

      // 登録成功ダイアログを表示する
      this.showRegisterSuccessModal = true;
    } catch (error) {
      // 利用者数の上限に達している場合、ステータスコード422が返却される
      // その場合はエラーメッセージを表示する
      // ステータスコード422以外は例外を投げなおして共通エラーハンドラに処理を委譲する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.errorMessage = "既に同一のメールアドレスが登録されているため登録できません。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 家族メールユーザー登録用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   * - 生年月日が入力されている場合は生年月日を結合してnumber型に変換する
   */
  public createRequestModel(): MailFamiliesPostModel {
    let birthday: any = undefined;
    if (this.year && this.month && this.day) {
      birthday = this.joinBirthday();
    }

    const requestModel: MailFamiliesPostModel = {
      familyMailaddress: this.email,
      familyName: this.$stringUtil.convertYen(this.name),
      familyTel: this.tel,
      familyRelation: this.$stringUtil.convertYen(this.relation),
      familyPosition: this.position ? this.$stringUtil.convertYen(this.position) : undefined,
      familyGender: this.gender,
      familyBirthDay: birthday
    };

    return requestModel;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

// 親要素からネストしてスタイルを指定する
#waiter-family-register {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
